/**
 * Where a line of music stands in its bar.
 *
 * A bar holds its music as a flat list of voices, and a voice belongs to its bar: the same
 * line of music is kept at one index in this bar and at another in the next, wherever a bar
 * between them has a voice more or a voice fewer on some staff above it. What a line keeps
 * from bar to bar is the staff it is on and which line of that staff it is, so that is what
 * anything following a line across a bar line goes by -- the caret, a slur as it is played,
 * and the tie that would rather stay in its own line.
 */

import type { EventId, MeasureId } from "./ids";
import type { Part, PartMeasure, Score } from "./types";

/** Where a bar's line stands: the staff, which line of that staff, and of which kind. */
export interface Standing {
  readonly staff: number;
  /** Which line of its own kind it is on that staff, counting from zero. */
  readonly ordinal: number;
  /** Whether it is the ossia offered above the staff rather than a voice played from it. */
  readonly ossia: boolean;
}

/** Indices into `measure.voices` of the lines of one kind on `staff`, in the order kept. */
export function linesOn(measure: PartMeasure, staff: number, ossia: boolean): readonly number[] {
  const found: number[] = [];
  measure.voices.forEach((voice, index) => {
    if (voice.staff === staff && voice.ossia === ossia) found.push(index);
  });
  return found;
}

/** Which staff a bar's line is on, and which line of that staff it is. */
export function standingIn(measure: PartMeasure, voiceIndex: number): Standing | null {
  const voice = measure.voices[voiceIndex];
  if (voice === undefined) return null;
  const ordinal = linesOn(measure, voice.staff, voice.ossia).indexOf(voiceIndex);
  return ordinal < 0 ? null : { staff: voice.staff, ordinal, ossia: voice.ossia };
}

/** The name a bar's line goes by where nothing says more: its staff, and which line of it. */
function standingName(measure: PartMeasure, voiceIndex: number): string {
  const standing = standingIn(measure, voiceIndex);
  if (standing === null) return `${voiceIndex}`;
  return `${standing.staff}/${standing.ossia ? "ossia " : ""}${standing.ordinal}`;
}

/** Where a slur begins or ends: which bar, and which of that bar's voices. */
interface SlurEnd {
  readonly bar: number;
  readonly voice: number;
}

/**
 * The name every line of a part goes by, bar by bar, index for index with `measure.voices`.
 *
 * Two voices of two bars with the same name are the same line of music. A line is named by
 * its staff and which line of that staff it is, which is all a bar says of it -- and a slur
 * over a bar line says more. It begins on a note of one voice and ends on a note of another
 * bar's, and those two are one line whatever each bar keeps them as: a bass line that is the
 * first voice of its staff in one bar and the second in the next, where a broken chord runs
 * down into that staff ahead of it. So the voice a slur ends in takes the name the slur began
 * with, and the voice that had that name takes the one given up. Slurs are read in the order
 * they end, so that the name a slur began with is settled before it is handed on, and a
 * voice one slur has named is not named again by another.
 *
 * An ossia is named apart from the voices played under it, so that it is never taken for one
 * of them.
 */
export function lineNames(score: Score, part: Part): ReadonlyMap<MeasureId, readonly string[]> {
  const names: string[][] = [];
  const ends = new Map<EventId, SlurEnd>();
  score.measures.forEach((global, bar) => {
    const measure = part.measures[global.id];
    const voices = measure === undefined ? [] : measure.voices;
    names.push(measure ? [...voices.keys()].map((voice) => standingName(measure, voice)) : []);
    voices.forEach((line, voice) => {
      for (const event of line.events) ends.set(event.id, { bar, voice });
    });
  });

  const slurs = part.spanners
    .filter((span) => span.kind === "slur")
    .flatMap((span) => {
      const from = ends.get(span.from);
      const to = ends.get(span.to);
      return from === undefined || to === undefined || from.bar >= to.bar ? [] : [{ from, to }];
    })
    .sort((one, other) => one.to.bar - other.to.bar || one.from.bar - other.from.bar);
  const settled = new Set<string>();
  for (const { from, to } of slurs) {
    const there = names[to.bar] ?? [];
    const begun = names[from.bar]?.[from.voice];
    const kept = there[to.voice];
    if (begun === undefined || kept === undefined) continue;
    const holder = there.indexOf(begun);
    const free = !settled.has(`${to.bar}/${to.voice}`) && !settled.has(`${to.bar}/${holder}`);
    if (begun !== kept && free) {
      there[to.voice] = begun;
      if (holder >= 0) there[holder] = kept;
    }
    settled.add(`${to.bar}/${to.voice}`);
  }
  return new Map(score.measures.map((global, bar) => [global.id, names[bar] ?? []]));
}
